import { useState } from 'react';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './components/ui/Button';
import { useClerk, useSignIn, useSignUp } from '@clerk/clerk-react';
import { isClientAuthDebug, clientAuthLog } from './lib/debug';
import { isSessionExists } from './lib/clerkErrors';


export default function SignIn() {
  const loc = useLocation();
  const { isLoaded, signIn } = useSignIn();
  const { isLoaded: isSignUpLoaded, signUp } = useSignUp();
  const { signOut } = useClerk();
  const [errorHint, setErrorHint] = useState<string | null>(null);
  const isDebug = isClientAuthDebug();
  const navigate = useNavigate();

  // Where RequireAuth sent the user from, so we can bounce back there after OAuth
  // completes (or after a session_exists race) instead of always landing on `/`.
  const intendedPath = useMemo(() => {
    const from = (loc.state as { from?: { pathname: string; search: string; hash: string } } | null)?.from;
    const path = from ? `${from.pathname}${from.search}${from.hash}` : undefined;
    return path && path !== '/sign-in' ? path : undefined;
  }, [loc.state]);

  const ssoReturnPath = useMemo(() => {
    if (intendedPath) return `/sso-return?return_to=${encodeURIComponent(intendedPath)}`;
    return '/sso-return';
  }, [intendedPath]);

  const signInUrl = useMemo(() => {
    const fromEnv = import.meta.env?.VITE_CLERK_SIGN_IN_URL as string | undefined;
    const base = fromEnv && typeof fromEnv === 'string' && fromEnv.trim().length > 0
      ? fromEnv.trim()
      // Fallback to production custom domain; safe default for prod while still working in dev
      : 'https://accounts.terra.clay.com/sign-in';
    // Build a canonical origin (strip www. to avoid losing hash on domain canonicalization)
    const rawOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    const canonicalOrigin = rawOrigin.replace('://www.', '://');
    // If base already has a query, append with &
    const joiner = base.includes('?') ? '&' : '?';
    // Deep-link to Google SSO to avoid the intermediate Clerk sign-in page
    // Set redirect_url directly to our sso-return route so the browser bounces back.
    const redirect = encodeURIComponent(`${canonicalOrigin}${ssoReturnPath}`);
    return `${base}${joiner}redirect_url=${redirect}&strategy=oauth_google`;
  }, [ssoReturnPath]);

  const signUpUrl = useMemo(() => {
    const fromEnv = import.meta.env?.VITE_CLERK_SIGN_IN_URL as string | undefined;
    const baseSignUp = fromEnv && typeof fromEnv === 'string' && fromEnv.trim().length > 0
      ? fromEnv.trim().replace(/\/sign-in(\/?|$)/, '/sign-up$1')
      : 'https://accounts.terra.clay.com/sign-up';
    const rawOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    const canonicalOrigin = rawOrigin.replace('://www.', '://');
    const joiner = baseSignUp.includes('?') ? '&' : '?';
    const redirect = encodeURIComponent(`${canonicalOrigin}${ssoReturnPath}`);
    return `${baseSignUp}${joiner}redirect_url=${redirect}&strategy=oauth_google`;
  }, [ssoReturnPath]);


  const onClick = async (e: any) => {
    // Use Clerk's redirect helper to keep SPA behavior
    e.preventDefault();
    try {
      if (!isLoaded || !signIn) {
        window.location.href = signInUrl;
        return;
      }
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        // Single rendezvous in our handler
        redirectUrl: ssoReturnPath,
        redirectUrlComplete: ssoReturnPath,
      });
    } catch (err: any) {
      const message = err?.message || '';
      if (isDebug) clientAuthLog('sign_in_redirect_fallback_used', { error: message });
      // Belt-and-suspenders: if the session became active between render and
      // click (race), Clerk throws `session_exists` for every attempt.
      // Sending the user into the app is the correct recovery — no retry needed.
      if (isSessionExists(err)) {
        navigate(intendedPath ?? '/', { replace: true });
        return;
      }
      // Retry with oidcPrompt=consent to recover stale/missing OAuth refresh tokens
      try {
        if (isLoaded && signIn) {
          if (isDebug) clientAuthLog('sign_in_consent_retry', {});
          await signIn.authenticateWithRedirect({
            strategy: 'oauth_google',
            redirectUrl: ssoReturnPath,
            redirectUrlComplete: ssoReturnPath,
            oidcPrompt: 'consent',
          });
          return;
        }
      } catch (consentErr: any) {
        if (isSessionExists(consentErr)) {
          navigate(intendedPath ?? '/', { replace: true });
          return;
        }
        if (isDebug) clientAuthLog('sign_in_consent_retry_failed', { error: consentErr?.message || String(consentErr) });
      }
      // If sign-in failed because the user doesn't exist, attempt sign-up with the same provider
      try {
        if (isSignUpLoaded && signUp) {
          await signUp.authenticateWithRedirect({
            strategy: 'oauth_google',
            redirectUrl: ssoReturnPath,
            redirectUrlComplete: ssoReturnPath,
          });
          return;
        }
      } catch (signUpErr: any) {
        if (isSessionExists(signUpErr)) {
          navigate(intendedPath ?? '/', { replace: true });
          return;
        }
        if (isDebug) clientAuthLog('sign_up_redirect_fallback_used', { error: signUpErr?.message || String(signUpErr) });
      }
      // Final fallback: stay on this page and show an actionable hint
      setErrorHint("We couldn't complete Google sign-in/sign-up. Please try again or use another browser.");
    }
  };

  const onSignUpClick = async (e: any) => {
    try {
      e?.preventDefault?.();
      if (!isSignUpLoaded || !signUp) return;
      await signUp.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: ssoReturnPath,
        redirectUrlComplete: ssoReturnPath,
      });
    } catch (err: unknown) {
      if (isSessionExists(err)) {
        navigate(intendedPath ?? '/', { replace: true });
        return;
      }
      setErrorHint('Could not start Google sign-up. Please try again.');
    }
  };

  // Show hint if SsoReturn detected an external account not found / session not created
  const reason = useMemo(() => {
    try { return new URLSearchParams(loc.search).get('reason') || undefined; } catch { return undefined; }
  }, [loc.search]);

  const isAccountNotFound = reason === 'external_not_found';
  // The server refused this email (lib/api.ts, 403 forbidden_domain). Signing
  // in with Google again keeps the same Clerk session and lands back here, so
  // the only way out is to sign out first.
  const isNotAllowed = reason === 'not_allowed';

  if (isNotAllowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-secondary px-4">
        <div className="w-full max-w-sm rounded-xl border border-primary bg-bg-primary p-6 shadow-sm">
          <h1 className="mb-2 text-lg font-semibold text-content-primary text-center">Access not allowed</h1>
          <p className="mb-4 text-sm text-content-secondary text-center">
            This Google account is not allowed to use Terra. Ask a Terra admin to add it, or sign in with your clay.com account.
          </p>
          <Button variant="indigo" className="w-full" onClick={() => { void signOut(); }}>
            Sign out and use another account
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-secondary px-4">
      <div className="w-full max-w-sm rounded-xl border border-primary bg-bg-primary p-6 shadow-sm">
        <h1 className="mb-2 text-lg font-semibold text-content-primary text-center">
          {isAccountNotFound ? 'Account not found' : 'Sign in to Terra'}
        </h1>

        {isAccountNotFound ? (
          <div className="mb-4 rounded-lg border border-tangerine-300 bg-tangerine-50 p-4">
            <p className="text-sm font-semibold text-tangerine-900 mb-2">You need to sign up before you can sign in.</p>
            <p className="text-xs text-tangerine-800">
              We couldn&apos;t find a Terra account for your Google login. Click the button below to create one.
            </p>
          </div>
        ) : null}

        {reason === 'slack_user_not_found' ? (
          <p className="mb-4 text-xs text-content-danger">You must join the Clay Slack workspace before signing in. Please contact blake.johnson@clay.com for an invite.</p>
        ) : null}
        {errorHint ? <p className="mb-4 text-xs text-content-danger">{errorHint}</p> : null}

        {isAccountNotFound ? (
          <>
            <a href={signUpUrl} rel="noopener noreferrer" onClick={(e) => { void onSignUpClick(e); }} className="block">
              <Button variant="indigo" className="w-full">Sign up with Google</Button>
            </a>
            <div className="mt-4 text-center text-xs text-content-placeholder">
              Already have an account?{' '}
              <a href={signInUrl} rel="noopener noreferrer" onClick={(e) => { void onClick(e); }} className="text-content-action hover:underline">
                Sign in
              </a>
            </div>
          </>
        ) : (
          <>
            <a href={signInUrl} rel="noopener noreferrer" onClick={(e) => { void onClick(e); }} className="block">
              <Button variant="indigo" className="w-full">Login with Google</Button>
            </a>
            <div className="mt-4 text-center text-xs text-content-placeholder">
              Don&apos;t have an account?{' '}
              <a href={signUpUrl} rel="noopener noreferrer" onClick={(e) => { void onSignUpClick(e); }} className="text-content-action hover:underline">
                Sign up
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
